/**
 * The app's `httpsCallable`: the Firebase one, plus a retry for refusals that
 * never reached our code.
 *
 * WHAT THIS EXISTS FOR. 2026-10-29, Clinical Dev: a user generating CRF forms
 * saw "Failed to save form - please retry". Cloud Logging showed five
 * `savecrfformv2` requests at the same instant answered HTTP 500 "The request
 * was aborted because there was no available instance". The service had scaled
 * to zero and a burst arrived before an instance started. Our function never
 * ran, and the refusal heals itself within seconds, but the page had no retry,
 * so it reached the customer as a failure.
 *
 * OPT-IN ONLY. The default is NO retry. A call opts in on the same options
 * object: pass `retryTransient: true` next to `timeout` in the options.
 * The flag is stripped before the SDK sees the options; everything else (the
 * timeout) is passed through unchanged. Opt in only a call that is safe to
 * repeat: read-only, or idempotent (a form save carries a saveId the server
 * recognises). Why not retry everything: in a browser a connection dropped
 * AFTER our code started is reported exactly like a refusal before it, so a
 * retry of a mutating call could run the work twice.
 *
 * WHAT COUNTS AS TRANSIENT (confirmed by reading @firebase/functions 0.11.8,
 * dist/index.esm.js, `codeForHTTPStatus` and `_errorForResponse`): the SDK's
 * own default descriptions, used when the response body is not our JSON.
 *  - code `functions/internal` with message exactly `internal` (HTTP 500 from
 *    the Google front end, or a fetch that failed outright, status 0).
 *  - code `functions/unavailable` with message exactly `unavailable` (HTTP 503
 *    with no body of ours).
 *  Both spellings of the code are accepted, with or without the `functions/`
 *  prefix, because the prefix has varied between SDK versions.
 *
 * WHAT IS NOT TRANSIENT, and why:
 *  - Uppercase `INTERNAL` (an uncaught exception in our v2 onCall): our code ran.
 *  - `unavailable` with any other message: our handlers throw
 *    HttpsError('unavailable', <our message>) (functions/src/utils/gemini.ts
 *    modelFailureHttpsError, crf/attachCrfRendering.ts), so our code ran.
 *  - `deadline-exceeded`: the server may still be running the work.
 *  - Every other code (already-exists, permission-denied, aborted,
 *    failed-precondition, and so on): a real answer from our code.
 *
 * Schedule: three retries (four attempts) after 1 s, 3 s and 6 s, each
 * multiplied by (0.5 + random()) so a burst of refused calls does not retry as
 * a burst. The last error is then rethrown unchanged. SDK 0.11.8 returns a
 * plain function with no extra properties (no `.stream`); any own properties a
 * newer SDK adds are copied onto the wrapper.
 */
import {
  httpsCallable as sdkHttpsCallable,
  type Functions,
  type HttpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions';

/** Shown when the final error after all retries is still a never-reached refusal. */
export const SERVER_DID_NOT_RESPOND_MESSAGE =
  'The server did not respond. Your change was not saved. Please try again in a minute.';

/** Base wait before retry 1, 2 and 3; each is jittered by (0.5 + random()). */
export const CALLABLE_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 6000];

/**
 * True when the error is a refusal that never reached our function code and is
 * safe to repeat. See the header for the exact rule.
 */
export function isTransientCallableError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { code, message } = err as { code?: unknown; message?: unknown };
  if (typeof code !== 'string') return false;
  const bare = code.startsWith('functions/') ? code.slice('functions/'.length) : code;
  // The SDK's default descriptions only. Anything else was written by our code.
  if (bare === 'unavailable') return message === 'unavailable';
  if (bare === 'internal') return message === 'internal';
  return false;
}

/** The SDK's timeout error, with or without the `functions/` prefix. */
function isDeadlineExceeded(err: unknown): boolean {
  const code = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
  return code === 'functions/deadline-exceeded' || code === 'deadline-exceeded';
}

/**
 * A seam so the error reporter can hear that a call finally gave up, without
 * this module importing it (`clientErrorReporting.ts` imports this file, so an
 * import the other way would be a cycle). The listener gets the callable's name
 * and nothing else: not the error, not the data.
 */
export type CallableFailureListener = (info: { callableName: string }) => void;

let failureListener: CallableFailureListener | null = null;

/** Register (or, with null, remove) the one listener. Called by the error reporter's installer. */
export function setCallableFailureListener(fn: CallableFailureListener | null): void {
  failureListener = fn;
}

/** The error reporter's own transport. Reporting its failure through itself would loop. */
const UNREPORTED_CALLABLES: readonly string[] = ['reportClientErrorV2'];

/**
 * Tell the listener a call finally gave up with a never-reached refusal.
 * Total: it never throws into the caller, and a listener that returns a
 * rejected promise is contained too. Our own HttpsErrors are real answers and
 * are not passed on.
 */
function notifyNotReached(name: string, err: unknown): void {
  if (!failureListener || UNREPORTED_CALLABLES.includes(name)) return;
  if (!isTransientCallableError(err)) return;
  try {
    const out = failureListener({ callableName: name }) as unknown;
    if (out && typeof (out as Promise<unknown>).then === 'function') {
      (out as Promise<unknown>).then(undefined, () => undefined);
    }
  } catch {
    /* a reporter must never change what the caller sees */
  }
}

const realSleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/** What `createRetryingCallable` can be told; the last two exist for tests. */
export interface RetryingCallableOptions {
  /** Retry transient refusals. Default false. */
  retry?: boolean;
  /**
   * The call is a READ, so one `deadline-exceeded` may also be retried (and
   * transient refusals, as with `retry`). Default false.
   */
  retryReadTimeout?: boolean;
  sleep?: (ms: number) => Promise<void>;
  random?: () => number;
}

/** The SDK's options plus the app's opt-in flag. */
export type AppHttpsCallableOptions = HttpsCallableOptions & {
  /** Retry refusals that never reached the server. Only for calls safe to repeat. */
  retryTransient?: boolean;
  /**
   * Only for a READ-ONLY call: also retry `deadline-exceeded`, once. Implies
   * `retryTransient`. The global rule refuses to retry a timeout because the
   * server may still be running the work, which is true of a write and
   * irrelevant to a read. Measured 2026-10-30 on Clinical Dev: listCrfFormsV2
   * cold-started in 88.6 s, the browser gave up at 70 s, and the CRF Builder
   * showed a study with 27 approved forms as empty. The retry meets a warm
   * instance.
   */
  retryReadTimeout?: boolean;
};

/**
 * Wraps one callable function. Without `retry` it calls straight through.
 */
export function createRetryingCallable<Req, Res>(
  inner: HttpsCallable<Req, Res>,
  name: string,
  opts: RetryingCallableOptions = {},
): HttpsCallable<Req, Res> {
  const { retry: retryTransientOpt = false, retryReadTimeout = false, sleep = realSleep, random = Math.random } = opts;
  const retry = retryTransientOpt || retryReadTimeout;
  // A call that does not opt in is returned untouched and reports nothing: in a
  // browser a connection dropped AFTER our code started looks exactly like a
  // refusal before it, so without the retry's evidence (four refusals in a row)
  // "never reached the server" would be a guess (checker finding, 2026-10-29).
  if (!retry) return inner;
  const wrapped = (async (data?: Req | null) => {
    let timeoutRetried = false;
    for (let attempt = 1; ; attempt++) {
      try {
        return await inner(data);
      } catch (err) {
        const base = CALLABLE_RETRY_DELAYS_MS[attempt - 1];
        // A timed-out READ is repeated once, and only when the caller said the
        // call is a read. A second timeout is a real answer: the error stands.
        if (retryReadTimeout && !timeoutRetried && isDeadlineExceeded(err)) {
          timeoutRetried = true;
          const wait = Math.round(CALLABLE_RETRY_DELAYS_MS[0] * (0.5 + random()));
          console.warn(`[callable] ${name} timed out (a read); retrying once in ${wait} ms`);
          await sleep(wait);
          continue;
        }
        if (base === undefined || !isTransientCallableError(err)) {
          // Out of retries on a never-reached refusal: the page is giving up.
          if (base === undefined) notifyNotReached(name, err);
          throw err;
        }
        const delay = Math.round(base * (0.5 + random()));
        console.warn(
          `[callable] ${name} was refused before reaching the server (attempt ${attempt} of ${CALLABLE_RETRY_DELAYS_MS.length + 1}); retrying in ${delay} ms`,
        );
        await sleep(delay);
      }
    }
  }) as HttpsCallable<Req, Res>;
  return Object.assign(wrapped, inner);
}

/**
 * Same signature as firebase/functions' `httpsCallable`, with the options
 * object also accepting `retryTransient: true`. The flag is removed before the
 * SDK is called; every other option is passed through.
 */
export function httpsCallable<RequestData = unknown, ResponseData = unknown>(
  functionsInstance: Functions,
  name: string,
  options?: AppHttpsCallableOptions,
): HttpsCallable<RequestData, ResponseData> {
  const { retryTransient, retryReadTimeout, ...sdkOptions } = options ?? {};
  const inner = sdkHttpsCallable<RequestData, ResponseData>(
    functionsInstance,
    name,
    options === undefined ? undefined : sdkOptions,
  );
  return createRetryingCallable<RequestData, ResponseData>(inner, name, { retry: retryTransient === true, retryReadTimeout: retryReadTimeout === true });
}

/**
 * A fresh id for one logical save. `crypto.randomUUID` throws outside secure
 * contexts and is missing on older Safari, so it is used when it works, then
 * `crypto.getRandomValues`, then time plus Math.random. Uniqueness, not
 * secrecy, is what is needed.
 */
export function newSaveId(): string {
  const c = (globalThis as { crypto?: Partial<Crypto> }).crypto;
  try {
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch { /* insecure context: fall through */ }
  try {
    if (c && typeof c.getRandomValues === 'function') {
      const bytes = c.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}
